import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { type Path, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { InstitutionBusinessProfileOrDefaults } from '@/features/institution/business-profile/schema';
import type { InstitutionDetailsV2BusinessProfileClients } from '@/services/api/profile';

type InstitutionBusinessProfileBreakdownName = 'clientTypeBreakdown' | 'clientGeographyBreakdown' | 'clientRiskBreakdown';

const BREAKDOWN_FIELDS = {
  clientTypeBreakdown: ['naturalPersonsPct', 'legalPersonsPct'],
  clientGeographyBreakdown: ['eea', 'uk', 'restOfEurope', 'americas', 'apac', 'middleEastAfrica'],
  clientRiskBreakdown: ['low', 'medium', 'high'],
} as const satisfies { [K in InstitutionBusinessProfileBreakdownName]: ReadonlyArray<keyof InstitutionDetailsV2BusinessProfileClients[K]> };

const BREAKDOWN_GRID_CLASS_NAMES = {
  clientTypeBreakdown: 'grid-cols-2',
  clientGeographyBreakdown: 'grid-cols-2',
  clientRiskBreakdown: 'md:grid-cols-3',
} as const satisfies Record<InstitutionBusinessProfileBreakdownName, string>;

type InstitutionBusinessProfileFieldsetBreakdownProps = {
  name: InstitutionBusinessProfileBreakdownName;
};

const InstitutionBusinessProfileFieldsetBreakdown = ({ name }: InstitutionBusinessProfileFieldsetBreakdownProps) => {
  const form = useFormContext<InstitutionBusinessProfileOrDefaults>();
  const { t } = useTranslation();
  const breakdown: Record<string, number | undefined> = form.watch(name) ?? {};
  const total = Object.values(breakdown).reduce<number>((sum, percentage) => sum + (percentage ?? 0), 0);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ fieldState: { error } }) => {
        const rootError = error?.message;

        return (
          <FormItem>
            <FormControl>
              <fieldset className="space-y-4">
                <legend className="font-medium">{t(`institutionDetails.businessProfile.${name}.title`)}</legend>
                <div className={`grid gap-4 ${BREAKDOWN_GRID_CLASS_NAMES[name]}`}>
                  {BREAKDOWN_FIELDS[name].map((key) => (
                    <FormField
                      key={key}
                      control={form.control}
                      name={`${name}.${key}` as Path<InstitutionBusinessProfileOrDefaults>}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t(`institutionDetails.businessProfile.${name}.${key}`)} (%)*</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              className="no-spinner"
                              inputMode="numeric"
                              type="number"
                              value={typeof field.value === 'number' ? field.value : ''}
                              onChange={(event) => field.onChange(event.target.value === '' ? undefined : Number(event.target.value))}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">{t('institutionDetails.businessProfile.breakdownTotal', { total })}</p>
              </fieldset>
            </FormControl>
            {rootError ? <FormMessage>{rootError}</FormMessage> : null}
          </FormItem>
        );
      }}
    />
  );
};
InstitutionBusinessProfileFieldsetBreakdown.displayName = 'InstitutionBusinessProfileFieldsetBreakdown';

export { InstitutionBusinessProfileFieldsetBreakdown };
